/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

/**
 * Document Migration Modal Validation Schema
 *
 * Ported from lego/page/browser/document-migration/FormValidation.jsx
 * Uses Yup for validation with TypeScript support
 */

import * as Yup from 'yup'
import {
  MAX_TOTAL_SIZE,
  MAX_FILE_SIZE,
  MAX_FILES_PER_UPLOAD,
  MIN_FILES_PER_UPLOAD,
  MAXIMUM_FILENAME_LENGTH,
  MAXIMUM_FILENAME_LENGTH_WINDOWS,
  VALIDATION_MESSAGES,
  WorkflowProcessingType,
  WF_STEP_TYPE
} from './constants'
import type { FileWithMetadata } from './types'

/**
 * Utility: Get file extension from filename
 */
export const getFilePathExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.')
  return lastDotIndex >= 0 ? filename.substring(lastDotIndex).toLowerCase() : ''
}

/**
 * Utility: Get filename without extension
 */
export const getFilePathWithoutExtension = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.')
  return lastDotIndex >= 0 ? filename.substring(0, lastDotIndex) : filename
}

/**
 * Utility: Truncate filename if too long
 */
export const truncateFilename = (filename: string, maxLength: number = 50): string => {
  if (!filename || filename.length < maxLength) {
    return filename
  }

  const lastPart = filename.slice(-maxLength / 2)
  const firstPart = filename.substring(0, maxLength - lastPart.length)

  return firstPart + '...' + lastPart
}

/**
 * Utility: Detect if running on Windows
 */
export const isWindows = (): boolean => {
  return navigator.platform.toLowerCase().includes('win')
}

/**
 * Utility: Get appropriate filename length limit
 */
export const getFilenameLengthLimit = (): number => {
  return isWindows() ? MAXIMUM_FILENAME_LENGTH_WINDOWS : MAXIMUM_FILENAME_LENGTH
}

/**
 * Validation schema for task team step label
 */
const taskTeamStepLabel = Yup.object()
  .shape({
    id: Yup.number().nullable(),
    label: Yup.string().nullable(),
    value: Yup.number().nullable()
  })
  .nullable()
  // FIX: Transform empty strings to null so Yup doesn't complain about the type
  .transform((value: any, originalValue: any) => {
    if (originalValue === '') return null
    return value
  })
  .default(null)

/**
 * Create workflow step schema with conditional usersGroups field requirement
 * @param requireUsersGroups - Whether usersGroups field should be required
 */
const createWorkflowStepSchema = (requireUsersGroups: boolean = false) => {
  return Yup.object().shape({
    taskTeamStepLabel: taskTeamStepLabel,

    processingType: Yup.string().nullable().required('Processing type is required'),

    nofAllValue: Yup.mixed()
      .nullable()
      .transform((value: any) => (value === '' || isNaN(value) ? null : value))
      .default(null) // Sets initial value to null when calling schema.cast() or formik reset
      .when('processingType', {
        is: WorkflowProcessingType.N_OF_ALL,
        then: (schema: any) =>
          schema
            .required(VALIDATION_MESSAGES.NOFALLVALUE_REQUIRED)
            .test('min-value', VALIDATION_MESSAGES.NOFALLVALUE_MIN, (value: any) => {
              // Ensure we are testing a number
              const numValue = parseFloat(value)
              return !isNaN(numValue) && numValue >= 1
            }),
        otherwise: (schema: any) => schema.nullable()
      }),

    // UserGroupSteps component uses usersGroups field, not merged
    merged: requireUsersGroups
      ? Yup.array().min(1, VALIDATION_MESSAGES.USERS_GROUPS_REQUIRED).required()
      : Yup.array().notRequired()
  })
}

/**
 * Validation schema for workflow step data (collaborators, reviewers, approvers)
 */
const createWorkflowStepDataSchema = (stepType: 'collaborators' | 'reviewers' | 'approvers') => {
  const defaultType = stepType === 'collaborators' ? WF_STEP_TYPE.USE_INITIATOR : WF_STEP_TYPE.NOT_DEFINED

  return Yup.object().shape({
    type: Yup.string().oneOf(Object.values(WF_STEP_TYPE)).default(defaultType),

    steps: Yup.array()
      .nullable()
      .default([])
      .when(['type', '$createCR'], {
        is: (type: string, createCR: boolean) => {
          if (stepType === 'collaborators') {
            return (
              (type === WF_STEP_TYPE.USE_PREDEFINED || type === WF_STEP_TYPE.SETUP_WITH_WORKFLOW) && createCR === true
            )
          } else if (stepType === 'approvers') {
            return type === WF_STEP_TYPE.SETUP_WITH_WORKFLOW && createCR === true
          }
          return false
        },
        then: (schema: any) => {
          const requireUsersGroups = stepType === 'collaborators' || stepType === 'approvers'
          return (
            schema
              .of(createWorkflowStepSchema(requireUsersGroups))
              // FIX: Add .min(1) to ensure [] is caught as an error
              .min(1, 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR')
              .required()
          )
        },
        otherwise: (schema: any) => {
          return schema.of(createWorkflowStepSchema(false)).notRequired()
        }
      })
  })
}

/**
 * Main validation schema for document migration form
 */
export const DocumentMigrationValidationSchema = (t: any) =>
  Yup.object().shape({
    /**
     * Files validation
     */
    files: Yup.array()
      .of(Yup.object())
      .default(() => [])
      .min(MIN_FILES_PER_UPLOAD, 'DOC_MIGRATION_ERROR_NO_DOC_SELECTED')
      // Explicit max-files test for correct validation message
      .test(
        'max-files',
        ({ value }: any) => {
          const excess = value.length - 50
          return t('DOC_MIGRATION_ERR_MAX_DOCUMENTS', {
            maxFiles: 50,
            toBeRemoved: excess
          })
        },
        function (value: any) {
          if (!value) return true
          return value.length <= 50
        }
      )
      .test('total-file-size', VALIDATION_MESSAGES.FILES_TOTAL_SIZE, (files: FileWithMetadata[] | undefined) => {
        if (!files || files.length === 0) return true
        const totalSize = files.reduce((sum: number, file: FileWithMetadata) => sum + file.size, 0)
        return totalSize <= MAX_TOTAL_SIZE
      })
      .test('filename-length', function (this: any, files: FileWithMetadata[] | undefined) {
        if (!files || !Array.isArray(files)) return true

        const maximumFilenameLength = 234

        const invalidFiles = files.filter((file: FileWithMetadata) => {
          const name = getFilePathWithoutExtension(file?.name || '')
          return name.length > maximumFilenameLength
        })

        // If no invalid files, return true (Validation Passes)
        if (invalidFiles.length === 0) return true

        const displayNames = invalidFiles.map((f: FileWithMetadata) => truncateFilename(f.name)).join(', ')

        // To make the test FAIL, you must return this.createError
        return this.createError({
          message: t('DOC_MIGRATION_ERR_MAX_FILE_NAME', {
            maximumFilenameLength: maximumFilenameLength,
            filesWithExceededNameLength: displayNames
          })
        })
      })
      .required('DOC_MIGRATION_ERROR_NO_DOC_SELECTED'),

    /**
     * Selected document type object (for reference, not validated)
     */
    selectedDocTypeObj: Yup.mixed().nullable(),

    /**
     * Type data validation (workflow configuration)
     */
    typeData: Yup.object().shape({
      collaborators: createWorkflowStepDataSchema('collaborators'),
      reviewers: createWorkflowStepDataSchema('reviewers'),
      approvers: createWorkflowStepDataSchema('approvers')
    }),

    /**
     * Change request validation
     */
    createCR: Yup.boolean().nullable().default(false).required(),

    description: Yup.string().max(254).required().default(''),

    /**
     * Expiration date validation
     */
    expirationDate: Yup.object()
      .nullable()
      .when('selectedDocTypeObj.requireExpirationDate', {
        is: true,
        then: (schema: any) => schema.required(),
        otherwise: (schema: any) => schema.nullable()
      }),

    /**
     * Other boolean flags
     */
    continueToEditPhase: Yup.boolean().nullable().default(false).required(),
    combineImages: Yup.boolean().required(),

    /**
     * Folder type validation (at root level)
     * Not required for site uploads or external uploads since it's set automatically
     */
    folderTypeTemplateId: Yup.mixed()
      .nullable()
      .test({
        name: 'folder-type-required',
        message: 'DOC_MIGRATION_ERROR_NO_FOLDER_TYPE_SELECTED',
        test: function (value: any) {
          const formValues = (this as any).parent as any

          // Not required for site uploads or external uploads
          if (formValues?.isSiteUpload === true || formValues?.isExternalUpload === true) {
            return true
          }

          // Required for regular migrations
          return value != null
        }
      }),

    /**
     * Document type validation (at root level)
     */
    documentTypeTemplateId: Yup.number()
      .min(0)
      .nullable()
      .transform((value: any) => (isNaN(value) ? null : value))
      .default(null)
      .required('DOC_MIGRATION_ERROR_NO_DOC_TYPE_SELECTED')
  })

/**
 * Helper: Validate form data and return errors
 */

/**
 * Helper: Validate single field
 */
